import {auth, apiClient, LANDING_URL, setUnauthorizedListener, signOutWithBackend} from "@/services";
import type {User, AuthContextType} from "@/types";
import {createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState} from "react";

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const signedOutOnLoad = auth.consumeSignOutFlag();

export const AuthProvider = ({children}: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [signedOut, setSignedOut] = useState(signedOutOnLoad);
    const [isLoading, setIsLoading] = useState(false);
    const [sessionError, setSessionError] = useState(false);

    const generationRef = useRef(0);
    const validatedRef = useRef(false);
    const userRef = useRef<User | null>(null);
    const inFlightRef = useRef<Promise<boolean> | null>(null);

    const invalidateSession = useCallback((): void => {
        generationRef.current += 1;
        validatedRef.current = false;
        userRef.current = null;
        inFlightRef.current = null;
        setUser(null);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        auth.clearLegacyUserCache();

        if (signedOutOnLoad) {
            window.location.replace(LANDING_URL);
        }
    }, []);

    useEffect(() => {
        setUnauthorizedListener(() => {
            if (validatedRef.current) {
                invalidateSession();
            }
        });
        return () => setUnauthorizedListener(null);
    }, [invalidateSession]);

    const ensureUser = useCallback((): Promise<boolean> => {
        if (validatedRef.current) {
            return Promise.resolve(userRef.current !== null);
        }

        if (inFlightRef.current) {
            return inFlightRef.current;
        }

        const generation = generationRef.current;
        setIsLoading(true);
        setSessionError(false);

        const request: Promise<boolean> = apiClient.getMe()
            .then((me) => {
                if (generation !== generationRef.current) {
                    return false;
                }

                validatedRef.current = true;
                userRef.current = me;
                setUser(me);
                return me !== null;
            })
            .catch(() => {
                if (generation === generationRef.current) {
                    setSessionError(true);
                }
                return false;
            })
            .finally(() => {
                if (inFlightRef.current === request) {
                    inFlightRef.current = null;
                }
                if (generation === generationRef.current) {
                    setIsLoading(false);
                }
            });

        inFlightRef.current = request;
        return request;
    }, []);

    const updateUser = useCallback((updatedUser: User): void => {
        userRef.current = updatedUser;
        setUser(updatedUser);
    }, []);

    const logout = useCallback(async (): Promise<void> => {
        auth.markSignOutInProgress();
        setSignedOut(true);
        invalidateSession();
        signOutWithBackend();
    }, [invalidateSession]);

    const value = useMemo<AuthContextType>(() => ({
        user,
        isLoading,
        sessionError,
        signedOut,
        ensureUser,
        updateUser,
        logout,
        isAuthenticated: !!user,
    }), [user, isLoading, sessionError, signedOut, ensureUser, updateUser, logout]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
